import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BkPage, Card, SmallBtn, fmtMoney, fmtTime } from "@/components/bookkeeping/BkPage";

const SYNC = { synced: ["#4CCC06", "Synced"], pending: ["#A3A3A3", "Pending"], error: ["#FF6B6B", "Error"] };
const EXP = { open: "#F5B700", partially_matched: "#F5B700", matched: "#4CCC06", void: "#666", written_off: "#666" };

export const SyncBadge = ({ sync, testId }) => {
  const [c, label] = sync?.voided ? ["#A3A3A3", "Voided in QBO"] : SYNC[sync?.status] || SYNC.pending;
  return <span title={sync?.error || ""} data-testid={testId} className="rounded-full border px-2 py-0.5 text-[10px] font-extrabold" style={{ color: c, borderColor: c }}>{label}</span>;
};

const ReviewRow = ({ t, reload }) => {
  const act = async (path, msg) => { try { await api(path, { method: "POST" }); toast.success(msg); reload(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="space-y-2 py-3" data-testid={`bk-review-${t.id}`}>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-neutral-400">{t.posted_date}</span>
        <span className="min-w-0 flex-1 font-bold text-white">{t.merchant_name || t.description}</span>
        <span className="font-extrabold" style={{ color: t.amount >= 0 ? "#4CCC06" : "#fff" }}>{fmtMoney(t.amount)}</span>
      </div>
      {t.proposals.map((m) => (
        <div key={m.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-[#262626] bg-[#0F0F0F] px-3 py-2 text-xs">
          <span className="min-w-0 flex-1 text-neutral-300">
            {m.match_type === "intercompany_candidate" ? <b className="text-[#F5B700]">Possible intercompany transfer</b>
              : <>Proposed: <b className="text-white">{m.expected?.label}</b> · {m.expected?.counterparty_name} · {fmtMoney(m.amount)}</>}
            <span className="block text-neutral-500">{Math.round(m.confidence * 100)}% · {m.reasoning}</span>
          </span>
          {m.match_type !== "intercompany_candidate" && <SmallBtn primary onClick={() => act(`/bookkeeping/matches/${m.id}/approve`, "Approved, posting to QuickBooks")} data-testid={`bk-approve-${m.id}`}>Approve</SmallBtn>}
          <SmallBtn onClick={() => act(`/bookkeeping/matches/${m.id}/undo`, "Sent back to the queue")} data-testid={`bk-reject-${m.id}`}>Not this</SmallBtn>
        </div>
      ))}
    </div>
  );
};

const Body = ({ entity }) => {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    const q = `entity_id=${entity.id}`;
    const [all, expected, matches] = await Promise.all([api(`/bookkeeping/review?${q}`), api(`/bookkeeping/expected?${q}`), api(`/bookkeeping/matches?${q}`)]);
    setD({ review: all.filter((t) => t.review_reason === "expected_match" || t.review_reason === "intercompany"), expected, matches });
  }, [entity.id]);
  useEffect(() => { setD(null); load().catch((e) => toast.error(e.message)); }, [load]);
  const run = async (path, msg) => { try { await api(path, { method: "POST" }); toast.success(msg); setTimeout(() => load(), 6000); } catch (e) { toast.error(e.message); } };
  if (!d) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <SmallBtn primary onClick={() => run(`/bookkeeping/pipeline/run?entity_id=${entity.id}`, "Matching and posting started")} data-testid="bk-run-pipeline">Match & post now</SmallBtn>
        <SmallBtn onClick={() => run("/bookkeeping/expected/rebuild", "Refreshing expected payments")} data-testid="bk-rebuild-expected">Refresh expected payments</SmallBtn>
        <SmallBtn onClick={() => run(`/bookkeeping/consistency/run?entity_id=${entity.id}`, "Checking against QuickBooks; see Job log")} data-testid="bk-run-consistency">Check against QuickBooks</SmallBtn>
        <SmallBtn onClick={load} data-testid="bk-matching-refresh">Refresh</SmallBtn>
      </div>
      <Card title={`PAYMENT MATCHES TO REVIEW · ${d.review.length}`} testId="bk-payment-review">
        {d.review.length === 0 ? <p className="text-sm text-neutral-500" data-testid="bk-payment-review-empty">No payment matches waiting.</p>
          : <div className="divide-y divide-[#262626]">{d.review.map((t) => <ReviewRow key={t.id} t={t} reload={load} />)}</div>}
        <Link to="/bookkeeping/review" className="mt-3 inline-block text-xs font-bold text-[#4CCC06]" data-testid="bk-open-review-queue">Open the full review queue (expenses, AI suggestions, bulk approve) →</Link>
      </Card>
      <Card title={`EXPECTED PAYMENTS · ${d.expected.length}`} testId="bk-expected-list">
        <div className="max-h-[420px] divide-y divide-[#1f1f1f] overflow-y-auto">
          {d.expected.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`bk-expected-${e.id}`}>
              <span className="w-24 text-neutral-400">{e.due_date || "–"}</span>
              <span className="min-w-0 flex-1 text-white">{e.label}<span className="text-neutral-500"> · {e.counterparty_name || "–"}</span>{e.is_seed && <span className="ml-2 text-[10px] font-bold text-[#A3A3A3]">TEST</span>}</span>
              <span className="font-bold text-white">{fmtMoney(e.amount)}</span>
              <span className="w-28 text-xs font-bold" style={{ color: EXP[e.status] }}>{e.status.replace("_", " ")}</span>
              <SyncBadge sync={e.sync} testId={`bk-sync-badge-${e.id}`} />
            </div>
          ))}
        </div>
      </Card>
      <Card title={`MATCHED · ${d.matches.length}`} testId="bk-matches-list">
        <div className="max-h-[360px] divide-y divide-[#1f1f1f] overflow-y-auto">
          {d.matches.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center gap-3 py-2 text-xs" data-testid={`bk-match-${m.id}`}>
              <span className="w-24 text-neutral-400">{m.txn?.posted_date}</span>
              <span className="min-w-0 flex-1 text-white">{m.txn?.description}<span className="block text-neutral-500">{m.match_type} · {m.reasoning}</span></span>
              <span className="font-bold text-white">{fmtMoney(m.amount)}</span>
              <span className="w-36 text-neutral-400">{m.qbo_payment_id ? `QBO payment #${m.qbo_payment_id}` : m.post_error ? <span className="text-[#FF6B6B]" title={m.post_error}>Post error</span> : m.match_type === "transfer" ? "Transfer" : "Waiting to post"}<span className="block">{fmtTime(m.posted_to_qbo_at)}</span></span>
              <SmallBtn onClick={() => run(`/bookkeeping/matches/${m.id}/undo`, "Undone; QuickBooks payment removed")} data-testid={`bk-undo-${m.id}`}>Undo</SmallBtn>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};

export default function Matching() {
  return <BkPage title="Matching" testId="bookkeeping-matching">{({ entity }) => <Body entity={entity} />}</BkPage>;
}
